import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

interface UpcomingEventsProps {
  nhSlug: string;
}

interface NHEvent {
  id: string;
  title: string;
  description: string | null;
  event_date: string;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  booking_url: string | null;
}

export function UpcomingEvents({ nhSlug }: UpcomingEventsProps) {
  const [events, setEvents] = useState<NHEvent[]>([]);

  useEffect(() => {
    const fetchEvents = async () => {
      const today = new Date().toISOString().split("T")[0];
      const { data } = await supabase
        .from("nursing_home_events")
        .select("*")
        .eq("nursing_home_id", nhSlug)
        .eq("is_published", true)
        .gte("event_date", today)
        .order("event_date", { ascending: true })
        .limit(3);
      if (data) setEvents(data);
    };
    fetchEvents();
  }, [nhSlug]);

  if (events.length === 0) return null;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2 bg-grandpal-beige/20">
        <CardTitle className="text-lg text-grandpal-green-dark flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Upcoming Events
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        {events.map((event) => (
          <div key={event.id} className="border-b last:border-0 pb-3 last:pb-0">
            <h4 className="font-semibold">{event.title}</h4>
            <p className="text-sm text-muted-foreground">
              {format(new Date(event.event_date), "EEEE, d MMMM yyyy")}
              {event.start_time && ` · ${event.start_time}`}
              {event.end_time && ` – ${event.end_time}`}
            </p>
            {event.description && (
              <p className="text-sm mt-1">{event.description}</p>
            )}
            {event.location && (
              <p className="text-xs text-muted-foreground mt-1">📍 {event.location}</p>
            )}
            {event.booking_url && (
              <Button variant="link" size="sm" className="p-0 h-auto mt-1" asChild>
                <a href={event.booking_url} target="_blank" rel="noopener noreferrer">
                  Book a spot <ExternalLink className="h-3 w-3 ml-1" />
                </a>
              </Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
